import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, IndianRupee, Wallet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useStudentPendingFees from '../../hooks/useStudentPendingFees';
import FeePayFlow from '../QrPayments/FeePayFlow';
import { convertToIndianCurrency } from '../../utils/common';
import './PayFeesDropdown.scss';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '';

const PayFeesDropdown = () => {
  const {
    studentId, items, totalPending, canPay, paymentEnabled, qrConfig, qrForFees, loading, refresh,
  } = useStudentPendingFees();
  // With only the QR, say so on the button — the student is about to scan, not to see a card form.
  const qrOnly = !paymentEnabled && qrForFees;
  const [open, setOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  if (loading || !canPay) return null;

  // Built once per click: FeePayFlow restarts its method choice when the target changes.
  const openPayModal = (item) => {
    setPayTarget({
      courseAssignmentId: item.courseAssignmentId || null,
      courseName: item.courseName,
      pending: item.pendingFees,
      defaultAmount: item.nextEmi?.balance ?? null,
    });
    setOpen(false);
  };

  const studentStub = {
    _id: studentId,
    pendingFees: payTarget?.pending ?? totalPending,
  };

  return (
    <>
      <div className="payfees-nav" ref={wrapperRef}>
        <button
          type="button"
          className="payfees-nav__btn"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-haspopup="true"
        >
          <Wallet size={17} />
          <span>Pay Fees</span>
          <span className="payfees-nav__badge">{items.length}</span>
        </button>

        {open && (
          <div className="payfees-panel" role="menu">
            <div className="payfees-panel__head">
              <div>
                <h3 className="payfees-panel__title">Pending Fees</h3>
                <p className="payfees-panel__sub">
                  Total due: <strong>{convertToIndianCurrency(totalPending)}</strong>
                </p>
              </div>
            </div>

            <ul className="payfees-panel__list">
              {items.map((item) => (
                <li key={item.assignmentId || item.courseName} className="payfees-panel__item">
                  <div className="payfees-panel__item-main">
                    <span className="payfees-panel__course">{item.courseName}</span>
                    {item.batchName && (
                      <span className="payfees-panel__batch">{item.batchName}</span>
                    )}
                    {item.nextEmi ? (
                      <>
                        <span className={`payfees-panel__emi${item.nextEmi.overdue ? ' is-overdue' : ''}`}>
                          {item.nextEmi.label}
                          {item.nextEmi.dueDate
                            ? ` · ${item.nextEmi.overdue ? 'overdue since' : 'due'} ${fmtDate(item.nextEmi.dueDate)}`
                            : ''}
                        </span>
                        <span className="payfees-panel__amount">
                          {convertToIndianCurrency(item.nextEmi.balance)}
                          <em> of {convertToIndianCurrency(item.pendingFees)} total</em>
                        </span>
                      </>
                    ) : (
                      <span className="payfees-panel__amount">
                        {convertToIndianCurrency(item.pendingFees)}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="payfees-panel__pay"
                    onClick={() => openPayModal(item)}
                  >
                    <IndianRupee size={12} />
                    {qrOnly ? 'Pay by QR' : item.nextEmi ? 'Pay EMI' : 'Pay Now'}
                  </button>
                </li>
              ))}
            </ul>

            <div className="payfees-panel__foot">
              <button
                type="button"
                className="payfees-panel__view"
                onClick={() => { setOpen(false); navigate('/s/fees'); }}
              >
                View all fees &amp; invoices <ArrowRight size={13} />
              </button>
            </div>
          </div>
        )}
      </div>

      <FeePayFlow
        target={payTarget}
        onClose={() => setPayTarget(null)}
        student={studentStub}
        paymentEnabled={paymentEnabled}
        qrConfig={qrConfig}
        onPaid={refresh}
        onQrSubmitted={refresh}
      />
    </>
  );
};

export default PayFeesDropdown;
